import { showNotification } from "#/src/utils/notification";
import { DefaultOptions, QueryClient, QueryClientConfig } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { clearSessionQueryData } from "./hooks/useSession";
import { checkIsCoppaError } from "#/src/hooks/api/parent/checkIsCoppaError";
import { setLoginRedirectIfUnset } from "#/src/utils/loginRedirect";
import * as logClient from "@classdojo/log-client";

interface ErrorDetail {
  type: number;
  message: string;
  detail?:
    | string
    | {
        error: string;
      };
  code?: string;
  expected?: true;
}

type AxiosErrorWithDetail = AxiosError<{ error: ErrorDetail }>;

const DEFAULT_ERROR_MESSAGE = "An error has occurred. Please try again in a few minutes or you may send us a message.";

const constructMessage = (error: AxiosErrorWithDetail) => {
  const errorData = error?.response?.data?.error;
  let message = DEFAULT_ERROR_MESSAGE;

  if (errorData?.detail) {
    if (typeof errorData.detail === "string") {
      message = errorData.detail;
    } else if (typeof errorData.detail === "object" && errorData.detail.error) {
      message = errorData.detail.error;
    }
  }

  return message;
};

const IGNORED_NETWORK_ERRORS: ReadonlySet<string | undefined> = new Set(["ERR_NETWORK", "ECONNABORTED"]);

type HandleErrorOptions = {
  ignoreExpected: boolean;
  notificationDetails: { title?: string; message?: string } | undefined;
};

// Extracted from `handleError` so the outer `unknown`-narrowing branch keeps
// the eslint complexity rule happy. Callers are guaranteed to have narrowed
// `error` via `axios.isAxiosError` before calling this.
const handleAxiosError = (error: AxiosErrorWithDetail, options: HandleErrorOptions) => {
  // Ignore certain error codes that won't include a server response. These error codes are
  // handled in the global axios config.
  if (IGNORED_NETWORK_ERRORS.has(error.code)) {
    return;
  }

  // This can be removed when we stop getting errors without a response here.
  // If we continue to see this, it's likely we want to add a code to the
  // IGNORED_NETWORK_ERRORS list.
  // See: https://app.datadoghq.com/logs?query=%40site%3Amarketplace%20%40log_type%3Aerror%20%22frontend_site.error%22%20%40context.type%3Atutor-axios-error-without-response&agg_m=count&agg_m_source=base&agg_q=status%2Cservice&agg_q_source=base%2Cbase&agg_t=count&clustering_pattern_field_path=message&cols=core_host%2Ccore_service&messageDisplay=inline&refresh_mode=sliding&saved-view-id=1923652&sort_m=%2C&sort_m_source=%2C&sort_t=%2C&storage=hot&stream_sort=desc&top_n=10%2C10&top_o=top%2Ctop&viz=stream&x_missing=true%2Ctrue&from_ts=1755016821390&to_ts=1755103221390&live=true
  if (!error.response) {
    // log error.code
    logClient.logMessage("WARN", "tutor-axios-error-without-response", {
      site: "marketplace",
      errorCode: error.code,
      errorName: error.name,
      errorMessage: error.message,
    });

    return;
  }

  const errorData = error.response.data.error;

  if (options.ignoreExpected && errorData?.expected) {
    return;
  }

  // if we get a 401 we should redirect to the login page
  if (errorData?.type === 401) {
    clearSessionQueryData();
    setLoginRedirectIfUnset();
    window.location.href = "#/login";
    return;
  }

  // we shouldn't show notification on COPPA error since we should show the COPPA consent modal instead
  if (checkIsCoppaError(error)) {
    return;
  }

  showNotification({
    title: options.notificationDetails?.title ?? errorData?.message ?? "Error",
    message: options.notificationDetails?.message ?? constructMessage(error),
    color: "red",
  });
};

export const handleError = (ignoreExpected = false, notificationDetails?: { title?: string; message?: string }) => {
  return (error: unknown) => {
    // Non-axios errors leaking into a query/mutation onError handler are
    // unexpected — log and bail rather than crash on `.response` access.
    if (!axios.isAxiosError<{ error: ErrorDetail }>(error)) {
      logClient.logMessage("WARN", "tutor-non-axios-error-in-handle-error", {
        site: "marketplace",
        errorString: String(error),
      });
      return;
    }
    handleAxiosError(error, { ignoreExpected, notificationDetails });
  };
};

// Queries are fresh for 5 minutes and don't refetch on window focus. Queries whose server state
// changes with time or outside this cache (live sessions, payment status, polling) set
// `staleTime: 0` (or `refetchOnWindowFocus: "always"`) explicitly.
const defaultOptions: DefaultOptions<unknown> = {
  queries: {
    onError: handleError(),
    retry: false,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  },
};

const config: QueryClientConfig = {
  defaultOptions,
};

export default new QueryClient(config);
